"use client";

import { ReactNode, useState, useEffect } from "react";
import { NavigationBar } from "@features/core/components/NavigationBar";
import { ArrowLeft, Menu, X } from "lucide-react";
import {
  usePathname,
  useRouter
} from "next/navigation";
import Link from "next/link";
import { useSidebar } from "@features/core/hooks/useSidebar";
import { Roles } from "@features/core/utils/roles";
import { Spinner } from "@heroui/react";
import { useAuthStore } from "@/features/core/stores";

/* ---------------------------------------------------------------- */
/* TOKEN VALIDATION (CLIENT SIDE — ONLY exp check)                    */
/* ---------------------------------------------------------------- */
function isTokenValidClient(token: string | null | undefined): boolean {
  if (!token) return false;

  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;

    const payloadBase64 = parts[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const padded =
      payloadBase64 + "=".repeat((4 - (payloadBase64.length % 4)) % 4);

    const payload = JSON.parse(atob(padded));

    if (typeof payload.exp !== "number") return false;

    const now = Math.floor(Date.now() / 1000);
    return payload.exp > now;
  } catch {
    return false;
  }
}

/* ---------------- BACK BUTTON ---------------- */
const DashboardBackPagination = () => {
  const pathname = usePathname();
  const router = useRouter();

  const folio = pathname.split("/").pop();
  const isMainDashboard = [
    "odts",
    "outsourcing",
    "users",
    "report",
    "profile",
    "admin-management",
    "daily-check",
    "logistic",
    "dashboard",
    "accounting",
    "human-resources",
  ].includes(folio || "");

  const handleBack = () => {
    const section = pathname.split("/")[2];
    const handles: Record<string, string> = {
      "admin-management": "/dashboard/human-resources",
      odts: "/dashboard/odts",
      outsourcing: "/dashboard/outsourcing",
      logistic: "/dashboard/logistic",
    };
    router.push(handles[section] || "/dashboard");
  };

  if (isMainDashboard) return null;

  return (
    <div className="flex h-16 px-4 bg-[#F5F7F6] items-center">
      <ArrowLeft
        size={20}
        color="#E30613"
        className="cursor-pointer"
        onClick={handleBack}
      />
      <h1 className="ml-4 text-lg font-semibold text-[#5A4A49]">
        Detalle
      </h1>
    </div>
  );
};

/* ---------------- LAYOUT ---------------- */
export default function DashboardLayout({
  children,
}: {
  children: ReactNode;
}) {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.currentUser);
  const accessToken = useAuthStore((s) => s.accessToken);
  const logout = useAuthStore((s) => s.logout);

  const router = useRouter();
  const pathname = usePathname();
  const { sidebar, validateRol } = useSidebar();

  const translateArea = (key: string) =>
    Roles.find((area) => area.key === key)?.name;

  /* ---------------------------------------------------------------- */
  /* 🔥 VALIDATE TOKEN ON EVERY NAVIGATION                              */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const token =
      localStorage.getItem("accessToken") || accessToken || null;

    if (!isTokenValidClient(token)) {
      logout();
      localStorage.removeItem("accessToken");

      if (pathname !== "/auth/login") {
        router.push("/auth/login");
      }
    }
  }, [pathname, accessToken, logout, router]);

  /* ---------------------------------------------------------------- */
  /* ⏱️ SAFETY NET: CHECK EVERY 60s EVEN WITHOUT NAVIGATION             */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const interval = setInterval(() => {
      const token =
        localStorage.getItem("accessToken") || accessToken || null;

      if (!isTokenValidClient(token)) {
        logout();
        localStorage.removeItem("accessToken");
        router.push("/auth/login");
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, [accessToken, logout, router]);

  if (!isAuthenticated || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F5F7F6]">
        <Spinner size="lg" color="danger" />
      </div>
    );
  }

  return (
<div className="flex min-h-screen bg-[#F5F7F6]">

  {/* SIDEBAR – SOLO DESKTOP */}
<aside
  className={`
    fixed inset-y-0 left-0 z-50
    h-dvh
    bg-[#E30613] text-white
    flex flex-col
    transition-[width] duration-300 ease-in-out

    /* mobile */
    w-64
    ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
    md:translate-x-0

    /* desktop */
    ${sidebarOpen ? "md:w-64" : "md:w-20"}
  `}
>

    {/* Header */}
    <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
      {sidebarOpen && (
        <h2 className="font-bold text-xl tracking-wide">
          Dashboard
        </h2>
      )}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="text-white p-2 rounded hover:bg-white/10"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>
    </div>

    {/* Navigation */}
    <nav className="flex-1 flex flex-col gap-2 px-6 py-6 overflow-y-auto">
      {sidebar.map((item) => (
        <Link
          key={item.key}
          href={item.path}
          className={`flex items-center gap-4 py-3 px-2 rounded-lg hover:bg-white/10 transition font-medium ${
            validateRol(item) ? "" : "hidden"
          }`}
        >
          <span className="text-white">{item.icon}</span>
          {sidebarOpen && (
            <span className="text-base font-semibold tracking-wide">
              {item.label}
            </span>
          )}
        </Link>
      ))}
    </nav>

    {/* Footer */}
    <div className="border-t border-white/20 px-6 py-5 mt-auto">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-[#E30613] font-bold text-lg">
          {user.fullName?.charAt(0).toUpperCase()}
        </div>

        {sidebarOpen && (
          <div className="flex flex-col">
            <p className="font-bold text-base">
              {user.fullName}
            </p>
            <p className="text-xs opacity-80">
              {user.email}
            </p>
            <p className="text-xs opacity-80 capitalize">
              {translateArea(user.area)}
            </p>
          </div>
        )}
      </div>

      {sidebarOpen && (
        <button
          onClick={() => {
            localStorage.removeItem("accessToken")
            logout()
            router.push("/auth/login")
          }}
          className="block text-md text-white/80 hover:text-white underline mt-2"
        >
          Log out
        </button>
      )}
    </div>
  </aside>

  {/* MAIN CONTENT */}
<main
  className={`
    flex-1 overflow-y-auto
    transition-[margin] duration-300 ease-in-out
    p-4 md:p-6
    pb-24 md:pb-6
    ${sidebarOpen ? "md:ml-64" : "md:ml-20"}
  `}
>

    <DashboardBackPagination />
    {children}
  </main>

  {/* MOBILE NAV – SOLO MOBILE */}
  <footer className="fixed bottom-0 w-full z-50 md:hidden">
    <NavigationBar />
  </footer>

</div>

  );
}
